import { CompetencyBar } from "@/components/shared";
import { cn } from "@/lib/utils";
import { CheckCircle2, TriangleAlert } from "lucide-react";

export function Stat({ value, label, tone, testId }) {
  return (
    <div className={cn("hs-card px-5 py-4", tone === "amber" && "border-amber-400/40")} data-testid={testId}>
      <div className={cn("hs-mono text-3xl font-semibold", tone === "primary" && "text-primary", tone === "amber" && "text-amber-600 dark:text-amber-300")}>{value}</div>
      <div className="text-xs text-muted-foreground mt-1">{label}</div>
    </div>
  );
}

export function CompetencyScores({ result, rubric }) {
  const weights = Object.fromEntries((rubric || []).map((r) => [r.name, r.weight]));
  const comps = (result?.competencies || []).filter((c) => c.score != null);
  return (
    <section className="hs-card p-5 sm:p-6 space-y-4" data-testid="report-scoring">
      <div>
        <h2 className="text-sm font-semibold">Score by competency</h2>
        <p className="text-xs text-muted-foreground mt-1">How your trial answers scored. This previews the scoring; it isn't a real candidate.</p>
      </div>
      {comps.length ? <div className="space-y-3">{comps.map((c) => <CompetencyBar key={c.name} name={c.name} score={Math.round(c.score)} weight={weights[c.name]} />)}</div>
        : <p className="text-sm text-muted-foreground">{result ? "No competency scores were returned for this trial." : "Finish the trial to see how it scores."}</p>}
      {result?.rationale && <p className="text-sm text-muted-foreground leading-6 border-t border-border pt-3">{result.rationale}</p>}
    </section>
  );
}

const SEG = { easy: "bg-emerald-500/25 text-emerald-800 dark:text-emerald-200", medium: "bg-sky-500/25 text-sky-800 dark:text-sky-200", hard: "bg-amber-500/30 text-amber-800 dark:text-amber-200" };

export function QualityChecks({ checks, diff }) {
  return (
    <section className="hs-card p-5 sm:p-6 space-y-4" data-testid="report-quality-checks">
      <h2 className="text-sm font-semibold">Quality checks</h2>
      <ul className="grid sm:grid-cols-2 gap-2.5">
        {checks.map((c) => (
          <li key={c.label} className="flex items-start gap-2 text-sm">
            {c.ok ? <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 shrink-0" /> : <TriangleAlert className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />}
            <span className={c.ok ? "" : "text-amber-700 dark:text-amber-300"}>{c.label}</span>
          </li>
        ))}
      </ul>
      <div className="space-y-1.5">
        <p className="text-xs text-muted-foreground">Difficulty mix</p>
        <div className="flex h-7 rounded-md overflow-hidden hs-mono text-[11px] font-semibold">
          {Object.entries(diff).map(([k, n]) => n > 0 && <div key={k} style={{ flexGrow: n }} className={cn("flex items-center justify-center min-w-[64px] capitalize", SEG[k])}>{k} {n}</div>)}
        </div>
      </div>
    </section>
  );
}

export function ProctoringCard({ result }) {
  const flags = result?.proctor_flags || [];
  return (
    <section className="hs-card p-5 space-y-3" data-testid="report-proctoring">
      <h2 className="text-sm font-semibold">Proctoring during trial</h2>
      {!result ? <p className="text-xs text-muted-foreground">Finish the trial to see monitoring signals.</p>
        : flags.length === 0 ? <p className="text-sm text-muted-foreground">No signals recorded.</p>
        : <ul className="space-y-2">{flags.map((f) => <li key={f.type} className="flex justify-between gap-3 text-sm"><span className="text-muted-foreground">{f.detail}</span><span className="hs-mono">{f.count}</span></li>)}</ul>}
      <p className="text-[11px] text-muted-foreground">Signals are advisory. A person reviews every flag.</p>
    </section>
  );
}
